import moment, {type Moment} from 'moment-timezone'
import type {NonEmptyArray} from '@freckle/non-empty'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  number,
  string,
  array,
  field,
  date,
  record,
  nullable,
  nonEmptyArray,
  mapStatic
} from '@freckle/parser'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import type {RlSkillUspIdT} from '@freckle/educator-entities/ts/common/types/rl-skill-usp-id'
import {parseElaParagraph} from '@freckle/educator-entities/ts/ela/articles/models/article'
import type {Paragraph} from '@freckle/educator-entities/ts/ela/articles/models/article'
import {
  curriculaClient,
  extractData,
  fancyClient
} from '@freckle/educator-entities/ts/openapi-clients'

// An ElaPassage parameterized by the type of its questions
type GenericElaPassageT<Q> = {
  uuid: string
  paragraphs: Array<Paragraph>
  questions: Array<Q>
  author: string | null | undefined
  datePublished: Moment | null | undefined
  context: NonEmptyArray<string> | null | undefined
}

export type ElaSkillsPracticeQuestionT = {
  uuid: string
  text: string
  options: Array<string>
  answersIndexes: Array<number>
}

export type ElaPassageT = GenericElaPassageT<ElaSkillsPracticeQuestionT>

const elaAdaptiveQuestionParser: ParserT<ElaSkillsPracticeQuestionT> = record({
  uuid: field(string(), 'id'),
  text: field(string(), 'question'),
  options: array(string()),
  answersIndexes: array(number())
})

const elaPassageParser: ParserT<ElaPassageT> = record({
  uuid: string(),
  paragraphs: Parser.map(array(parseElaParagraph), 'filterDeprecatedPicture', paragraphs =>
    paragraphs.filter(({tag}) => tag !== 'picture')
  ),
  questions: array(elaAdaptiveQuestionParser),
  author: nullable(string()),
  datePublished: nullable(mapStatic(date(), moment)),
  context: nullable(nonEmptyArray(string()))
})

export const parseElaPassage = Parser.mkRun<ElaPassageT>(elaPassageParser)

export async function fetchElaPassage(passageUuid: string): Promise<ElaPassageT> {
  return curriculaClient
    .GET('/ela/skills-practice/passages/{uuid}.json', {
      params: {
        path: {
          uuid: passageUuid
        }
      }
    })
    .then(extractData)
    .then(parseElaPassage)
}

const elaPassageShell: ParserT<{uuid: string; skillId: RlSkillUspIdT | undefined | null}> = record({
  uuid: string(),
  skillId: nullable(string())
})

export const parseElaPassageShell = Parser.mkRun<{uuid: string; skillId?: RlSkillUspIdT | null}>(
  elaPassageShell
)

const fetchElaPassages = (passages: Array<{uuid: string}>) =>
  Promise.all(passages.map(p => fetchElaPassage(p.uuid)))

export async function fetchElaPassagesForAdaptiveAssignment(
  assignmentId: number,
  studentId: number
): Promise<Array<ElaPassageT>> {
  return await fancyClient
    .GET(
      '/2/ela-assignments/adaptive-skills/{ElaAdaptiveSkillsPassagesR-ElaAdaptiveSkillsPracticeAssignmentId}/passages',
      {
        params: {
          path: {
            'ElaAdaptiveSkillsPassagesR-ElaAdaptiveSkillsPracticeAssignmentId': assignmentId
          },
          query: {
            'student-id': studentId
          }
        }
      }
    )
    .then(extractData)
    .then(fetchElaPassages)
}

export async function fetchElaPassagesForTargetedAssignment(
  assignmentId: number
): Promise<Array<ElaPassageT>> {
  const path = Products.getPaths('ela_targeted_skills_practice')
  return await fancyClient
    .GET(
      '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/assignments/{SubjectProductAssignmentP-Int64}/passages',
      {
        params: {
          path: {
            'SubjectP-Subject': path.subjectPath,
            'ProductP-AbbreviatedProduct': path.productPath,
            'SubjectProductAssignmentP-Int64': assignmentId.toString()
          }
        }
      }
    )
    .then(extractData)
    .then(fetchElaPassages)
}

export async function fetchElaPassagesForFocusSkillsAssignment(
  assignmentId: number
): Promise<Array<ElaPassageT>> {
  const path = Products.getPaths('ela_focus_skills_practice')
  return await fancyClient
    .GET(
      '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/assignments/{SubjectProductAssignmentP-Int64}/passages',
      {
        params: {
          path: {
            'SubjectP-Subject': path.subjectPath,
            'ProductP-AbbreviatedProduct': path.productPath,
            'SubjectProductAssignmentP-Int64': assignmentId.toString()
          }
        }
      }
    )
    .then(extractData)
    .then(fetchElaPassages)
}
